import { Suspense, useEffect, useState } from 'react'
import { INBOX_ID, zonePlace } from '@terrazzo/core'
import { AppShell } from './components/AppShell'
import { useMe, useMySettings, useStructure, useSyncStatus, useToday } from './store/hooks'
import { actions } from './store/actions'
import { showToast } from './store/toasts'
import { deviceZone, homeZone } from './lib/clock'
import { store } from './store/store'
import { navigate, openSheet, useLocation } from './lib/router'
import { setHapticsEnabled, isMac } from './lib/haptics'
import { undo, redo } from './store/undo'
import { setBadge } from './lib/push'
import Login from './views/Login'
import Welcome, { needsOnboarding } from './views/Welcome'
import { Logo } from './components/Logo'
import { ErrorBoundary } from './components/ErrorBoundary'
import { preloadSheets } from './components/SheetHost'
import { preloadable } from './lib/preloadable'
import { applyTextSize } from './lib/scale'

const Board = preloadable(() => import('./views/Board'))
const Days = preloadable(() => import('./views/Days'))
const ItemPage = preloadable(() => import('./views/ItemPage'))
const Notes = preloadable(() => import('./views/Notes'))
const PagesView = preloadable(() => import('./views/PagesView'))
const Search = preloadable(() => import('./views/Search'))
const DoneLog = preloadable(() => import('./views/DoneLog'))
const Activity = preloadable(() => import('./views/Activity'))
const Chat = preloadable(() => import('./views/Chat'))
const SavedView = preloadable(() => import('./views/SavedView'))
const Settings = preloadable(() => import('./views/settings/Settings'))

/**
 * Warm the lazy chunks once the first screen is up. Besides instant tab switches, iOS only
 * raises the keyboard when focus happens inside the tap itself — a sheet or "New card"
 * page still downloading its code on first use would open without the keyboard.
 */
function usePreload() {
  useEffect(() => {
    const idle = (window as Window & { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number }).requestIdleCallback ?? ((f: () => void) => setTimeout(f, 1200))
    idle(
      () => {
        for (const v of [Board, Days, ItemPage, Notes, PagesView, Search, DoneLog, SavedView, Settings]) void v.preload().catch(() => {})
        preloadSheets()
        void import('./components/BodyEditor').catch(() => {})
      },
      { timeout: 3000 },
    )
  }, [])
}

function useTheme(theme: string) {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && mq.matches)
      document.documentElement.dataset.theme = dark ? 'dark' : 'light'
      document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', dark ? '#0a0a0a' : '#ffffff'))
    }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [theme])
}

/** Desktop shortcuts: `/` search, n = new, Cmd/Ctrl+Z undo (SPEC §6.2, §7.7). */
function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
      const mod = isMac ? e.metaKey : e.ctrlKey
      if (mod && e.key.toLowerCase() === 'z' && !t?.closest?.('.ProseMirror') && !(t && t.tagName === 'INPUT')) {
        e.preventDefault()
        if (e.shiftKey) redo()
        else undo()
        return
      }
      const sheetOpen = !!(history.state as { sheet?: unknown } | null)?.sheet
      if (mod && e.key.toLowerCase() === 'k' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        if (!sheetOpen) openSheet('command')
        return
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return
      // Space opens the command menu (Blender-style) unless it would press a focused control.
      if (e.key === ' ' && !e.shiftKey && !e.repeat && !sheetOpen && !t?.closest?.('button, a[href], summary, [role="button"], [role="checkbox"], [role="option"], label')) {
        e.preventDefault()
        openSheet('command')
        return
      }
      if (e.key === '/') {
        e.preventDefault()
        navigate('/search')
      } else if (e.key === 'n') {
        e.preventDefault()
        openSheet('add')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

/** App badge: Inbox items + today's open tasks (SPEC §10). */
/**
 * This phone's time zone, kept on the person once synced: their reminders (Morning of, Day
 * before), quiet hours and Google Calendar follow it. When it changes (a trip), a one-time note.
 */
function useDeviceZone() {
  const synced = useSyncStatus().synced
  const me = useMe().user?.id
  useEffect(() => {
    if (!synced || !me) return
    const check = () => {
      const zone = deviceZone()
      const was = store.userSettings(me).timezone
      if (zone === was) return
      actions.setMine('timezone', zone)
      if (was) showToast(`Now on ${zonePlace(zone)} time`, { detail: zone === homeZone() ? 'Home again' : `Times on tasks stay ${zonePlace(homeZone())} time` })
    }
    check()
    document.addEventListener('visibilitychange', check)
    return () => document.removeEventListener('visibilitychange', check)
  }, [synced, me])
}

/**
 * The app icon's badge: open Inbox items plus what's due today. Its own leaf, listening only to
 * structural changes (done, dates, moves, trash), so typing doesn't redraw the whole app.
 */
function BadgeSync() {
  const v = useStructure()
  const today = useToday()
  useEffect(() => {
    const t = setTimeout(() => {
      const tree = store.tree
      let n = 0
      for (const id of tree.childrenOf.get(INBOX_ID) ?? []) if (!tree.inTrash.has(id) && !tree.inArchive.has(id) && store.get(id)?.done !== true) n++
      for (const it of store.idx.items.values()) if (it.date === today && it.done === false && !tree.inTrash.has(it.id) && !tree.inArchive.has(it.id)) n++
      setBadge(n)
    }, 1000)
    return () => clearTimeout(t)
  }, [v, today])
  return null
}

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Logo size={48} className="animate-pulse" />
    </div>
  )
}

export function App() {
  const me = useMe()
  const loc = useLocation()
  const mine = useMySettings()
  const [onboarding, setOnboarding] = useState(() => needsOnboarding())
  useTheme(mine.theme)
  useEffect(() => applyTextSize(mine.textSize), [mine.textSize])
  usePreload()
  useShortcuts()
  useDeviceZone()
  useEffect(() => {
    setHapticsEnabled(mine.haptics)
  }, [mine.haptics])

  // Deep links & share target handoff.
  useEffect(() => {
    const p = loc.search
    if (p.get('share')) {
      const id = p.get('share')!
      const text = [p.get('title'), p.get('text'), p.get('url')].filter(Boolean).join('\n')
      history.replaceState({}, '', '/')
      if (id === '1') openSheet('add', { text })
      else openSheet('add', { shareId: id })
    } else if (p.get('add') === '1') {
      history.replaceState({}, '', location.pathname)
      openSheet('add')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (me.status === 'loading' && !me.user) return <Splash />
  if (!me.user) return <Login />
  if (loc.route.name === 'login') {
    navigate('/', { replace: true })
    return <Splash />
  }
  if (onboarding) return <Welcome onDone={() => setOnboarding(false)} />

  const home = String(mine.homeView || 'board')
  const route = loc.route.name === 'home' ? (home.startsWith('view:') ? { name: 'view' as const, id: home.slice(5) } : { name: home as 'board' }) : loc.route

  return (
    <AppShell>
      <BadgeSync />
      <ErrorBoundary
        key={loc.path}
        label="this screen"
        fallback={
          <div className="mx-auto max-w-sm px-6 py-24 text-center text-sm text-muted">
            Something on this screen failed to load.{' '}
            <button type="button" className="underline" onClick={() => location.reload()}>
              Reload
            </button>
          </div>
        }
      >
        <Suspense fallback={<div className="h-dvh" />}>
          {route.name === 'board' && <Board />}
          {route.name === 'days' && <Days />}
          {route.name === 'notes' && <Notes />}
          {route.name === 'pages' && <PagesView />}
          {route.name === 'search' && <Search />}
          {route.name === 'done' && <DoneLog />}
          {route.name === 'activity' && <Activity />}
          {route.name === 'chat' && <Chat />}
          {route.name === 'item' && <ItemPage key={route.id} id={route.id} />}
          {route.name === 'view' && <SavedView key={route.id} id={route.id} />}
          {route.name === 'settings' && <Settings section={route.section} />}
          {(route.name === 'notfound' || route.name === 'welcome') && <Board />}
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  )
}
